// src/components/features/chat/MessageBubble.jsx
// Navy/dark user bubbles, gold accents, --bg-surface widget cards — themed via
// var(--token) throughout (see the C constant this replaced: a fixed navy/
// gold/cream palette that never changed with the patient's theme, which is
// why the assistant screen stayed light even when the patient was in DARK).

import React, { useState } from 'react';
import ToolStatusCard from './ToolStatusCard';
import AssistantMark from './AssistantMark';
import MarkerTrendWidget from './MarkerTrendWidget';
import TriageReportCard from './TriageReportCard';
import LabTestPaymentCard from './LabTestPaymentCard';
import AppointmentPaymentCard from './AppointmentPaymentCard';
import { TRIAGE_STAGES } from '../../healthrecords/useAutoTriage';
import { RECORD_TYPES_QUICK } from '../../../constants/recordTypes';
import Icon from '../../Icon';
import textLang from '../../../utils/textLang';
import { chatTranslationLabels } from '../../../constants/chatTranslationLabels';
import { hasSafetyFlag } from '../../../services/chatTranslateAPI';

// 17 Jul 2026: chat-based upload (Sprint 15). 'UPLOADING' precedes
// useAutoTriage's own pipeline (the raw multipart save), so it isn't part of
// its TRIAGE_STAGES enum — added here, everything else reuses those exact
// labels so the wording matches the dedicated /patient/upload page exactly.
const UPLOAD_STAGE_LABELS = {
  UPLOADING: 'Uploading your report…',
  ...Object.fromEntries(Object.entries(TRIAGE_STAGES).map(([k, v]) => [k, v.label])),
};

// The assistant's avatar, wherever it appears in the transcript — one mark,
// shared with the chat header and the welcome bubble (AssistantMark.jsx).
function AssistantAvatar({ size = 32 }) {
  return <AssistantMark size={size} />;
}

// Ask Previu in Malayalam / Hindi (useChat.js): the line under a translated
// message. Under the patient's own message, the English Previu actually
// received; under a reply, "Translated by AI" with the English one tap away —
// shown straight away, under a warning, when the safety check flagged the
// translation (a handling note alone doesn't); or, when translation failed, a note that the reply is in
// English. Labels are in the patient's language. Renders nothing for an
// untranslated message, so English-only chat is unchanged.
function TranslationNote({ message, isUser }) {
  const { english, flags, translationFailed, translationLang } = message;
  // Handling notes (e.g. ESCALATION_KEPT_IN_ENGLISH) don't warn; any other
  // flag does — see chatTranslateAPI.js.
  const flagged = !isUser && hasSafetyFlag(flags);
  const [open, setOpen] = useState(flagged);
  if (!english && !translationFailed) return null;

  const labels = chatTranslationLabels(translationLang);
  const className = `chat-translation${isUser ? ' chat-translation--user' : ''}`;

  if (translationFailed) {
    return (
      <div className={className} lang={translationLang}>
        <span className="chat-translation__meta">{labels.couldNotTranslate}</span>
      </div>
    );
  }

  const toggle = (
    <button type="button" className="chat-translation__toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
      {isUser
        ? (open ? labels.hideUnderstood : labels.seeUnderstood)
        : (open ? labels.hideEnglish : labels.seeEnglish)}
    </button>
  );

  return (
    <div className={className}>
      {flagged && (
        <div className="chat-translation__warning" role="note" lang={translationLang}>{labels.pleaseCheck}</div>
      )}
      <div className="chat-translation__meta" lang={translationLang}>
        {!isUser && <><span>{labels.translatedByAi}</span><span aria-hidden="true">·</span></>}
        {toggle}
      </div>
      {open && <div className="chat-translation__english" lang="en">{english}</div>}
    </div>
  );
}

export default function MessageBubble({ message, timestamp, compact, onConfirmUploadType, showAvatar = true }) {
  const { role, text, streaming, toolStatuses = [], error } = message;

  // 17 Jul 2026: document-type picker (Sprint 15 follow-up). Shown right
  // after a 📎 attach, before anything is uploaded — closes the earlier
  // mistagging gap (chat used to guess BLOOD_TEST vs OTHER from file format
  // alone) by asking the patient what the document actually is. Chips call
  // back into useChat.js's confirmUploadType(), which is what actually
  // fires the upload with the chosen recordType.
  if (role === 'document_type_picker') {
    const { pickerId, fileName, answered, chosenType, suggestedType } = message;
    const chosenConfig = answered ? RECORD_TYPES_QUICK.find(t => t.value === chosenType) : null;

    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <div style={{
          maxWidth: 300, minWidth: 0, padding: '12px 16px', borderRadius: '4px 16px 16px 16px',
          background: 'var(--amber-bg)', border: `1.5px solid var(--amber)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="paperclip" size={14} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fileName || 'Your file'}
            </span>
          </div>

          {answered ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Saved as {chosenConfig ? `${chosenConfig.icon} ${chosenConfig.label}` : chosenType}
            </div>
          ) : (
            <>
              {/* DEF-015: the suggested type is pre-highlighted, so the common
                  case is confirming one chip rather than choosing among five.
                  With no filename signal suggestedType is null and this reads as
                  the original open question. */}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                {suggestedType
                  ? 'This looks like a ' +
                    (RECORD_TYPES_QUICK.find(t => t.value === suggestedType)?.label
                      || 'document').toLowerCase() +
                    ' — tap to confirm, or pick another.'
                  : 'What kind of document is this?'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {RECORD_TYPES_QUICK.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => onConfirmUploadType?.(pickerId, t.value)}
                    style={{
                      padding: '6px 12px', borderRadius: 20,
                      // Suggested chip is filled rather than outlined — the
                      // difference has to be visible at a glance for the
                      // pre-selection to save the patient anything.
                      border: `1px solid var(--amber)`,
                      background: t.value === suggestedType ? 'var(--amber)' : 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontSize: 12,
                      fontWeight: t.value === suggestedType ? 700 : 500,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // 17 Jul 2026: chat-based upload progress (Sprint 15). Lives entirely as a
  // display-only widget message — the actual upload/triage pipeline runs in
  // useChat.js's confirmUploadType(), which keeps updating this same
  // message's `stage` field in place (matched by uploadId, not array index).
  if (role === 'upload_status') {
    const isFailed  = message.stage === 'FAILED';
    const isSkipped = message.stage === 'SKIPPED';
    const isDone    = message.stage === 'DONE';
    const label = isSkipped
      ? 'Saved — only PDF blood test reports are auto-analyzed today.'
      : (UPLOAD_STAGE_LABELS[message.stage] || 'Working on it…');

    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <div style={{
          maxWidth: 280, minWidth: 0, padding: '12px 16px', borderRadius: '4px 16px 16px 16px',
          background: isFailed ? 'var(--red-bg)' : 'var(--amber-bg)',
          border: `1.5px solid ${isFailed ? 'var(--red)' : 'var(--amber)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14 }}>{isFailed ? '⚠' : isDone ? '✅' : '📎'}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
              {message.fileName || 'Your report'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: isFailed ? 'var(--red)' : 'var(--text-muted)' }}>
            {isFailed ? (message.errorText || 'Something went wrong — please try again.') : label}
          </div>
          {!isFailed && !isDone && !isSkipped && (
            <div style={{ marginTop: 8, height: 3, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: '40%', background: 'var(--brand-accent)', borderRadius: 2,
                animation: 'upload-progress 1.2s ease-in-out infinite',
              }} />
            </div>
          )}
        </div>
        <style>{`
          @keyframes upload-progress {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(350%); }
          }
        `}</style>
      </div>
    );
  }

  // Sprint 13 (20 Jun): inline marker trend widget
  // ⚠ Constitution §III: displays raw data only — no clinical interpretation.
  // ⚠ PENDING Dr. Ajay Kumar sign-off before go-live.
  if (role === 'marker_trend') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <MarkerTrendWidget
          data={message.data}
          markerName={message.markerName}
          unit={message.unit}
        />
      </div>
    );
  }

  // 15 Jul 2026: inline triage report card — rendered when the
  // download_triage_report tool resolves. Clicking the card triggers the
  // actual print/download (see TriageReportCard.jsx); this bubble is purely
  // the card container, same layout pattern as marker_trend above.
  if (role === 'triage_report_card') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <TriageReportCard
          patient={message.patient}
          triageResult={message.triageResult}
          triageHistory={message.triageHistory}
          reportId={message.reportId}
          drawDate={message.drawDate}
          riskLevel={message.riskLevel}
          riskScore={message.riskScore}
        />
      </div>
    );
  }

  // 15 Jul 2026: inline lab-test payment card — rendered when create_order
  // resolves. Clicking the card is what actually opens Razorpay Checkout
  // and, on success, creates the Thyrocare order (see LabTestPaymentCard.jsx);
  // this bubble is purely the card container, same layout pattern as the
  // two card types above.
  if (role === 'lab_test_payment_card') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <LabTestPaymentCard
          patient={message.patient}
          panelName={message.panelName}
          testCode={message.testCode}
          orderPayload={message.orderPayload}
          summary={message.summary}
          savedAddresses={message.savedAddresses}
          selectedAddressId={message.selectedAddressId}
        />
      </div>
    );
  }

  // 16 Jul 2026: inline appointment payment card — same pattern as
  // lab_test_payment_card above, for doctor appointments.
  if (role === 'appointment_payment_card') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end', minWidth: 0,
        gap: 8, marginBottom: compact ? 10 : 14,
      }}>
        <AssistantAvatar size={compact ? 28 : 32} />
        <AppointmentPaymentCard
          patient={message.patient}
          doctorId={message.doctorId}
          doctorName={message.doctorName}
          appointmentType={message.appointmentType}
          appointmentPayload={message.appointmentPayload}
        />
      </div>
    );
  }

  const isUser = role === 'user';
  const avatarSz = compact ? 28 : 32;
  // A reply waiting on its translation keeps the typing cursor and hides the
  // English streaming in underneath it (useChat.js).
  const shownText = message.awaitingTranslation ? '' : text;
  const showCursor = streaming || message.awaitingTranslation;

  return (
    <div style={{
      display: 'flex',
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-end',
      gap: 8,
      marginBottom: compact ? 10 : 14,
      animation: 'slide-up 0.18s ease',
    }}>
      {/* Assistant avatar — once per run of consecutive assistant messages, on
          the last of them, so four replies in a row are one speaker rather than
          four restatements of who is talking. The space is still reserved on
          the others so the bubbles stay in one column. */}
      {!isUser && (showAvatar
        ? <AssistantAvatar size={avatarSz} />
        : <div style={{ width: avatarSz, flexShrink: 0 }} />)}

      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        maxWidth: compact ? '82%' : '75%',
        gap: 4,
      }}>
        {/* Tool status cards */}
        {!isUser && toolStatuses.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
            {toolStatuses.map((ts, i) => (
              <ToolStatusCard
                key={`${ts.toolName}:${ts.action ?? ''}-${i}`}
                toolName={ts.toolName}
                action={ts.action}
                status={ts.status}
              />
            ))}
          </div>
        )}

        {/* Bubble */}
        {(shownText || showCursor) && (
          <div lang={textLang(shownText)} style={{
            padding: compact ? '9px 13px' : '11px 15px',
            borderRadius: isUser
              ? '18px 18px 4px 18px'
              : '18px 18px 18px 4px',
            // User: --brand-dark (always dark in both themes, matches app
            // header/sidebar) with --text-on-brand-dark (always white) — not
            // --text-inverse, which flips per-theme and would go near-black
            // on this same near-black background in dark theme (the exact
            // bug --text-on-brand-dark's own index.css comment documents).
            // Assistant: --bg-surface card with a --border edge (matches
            // app cards in both themes).
            background: isUser ? 'var(--brand-dark)' : 'var(--bg-surface)',
            color: isUser ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
            fontSize: compact ? 13 : 14,
            lineHeight: 1.65,
            boxShadow: isUser
              ? '0 1px 6px rgba(13,30,53,0.25)'
              : '0 1px 3px rgba(0,0,0,0.06)',
            border: isUser ? 'none' : '1px solid var(--border)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            ...(error ? { borderColor: 'var(--red)', background: 'var(--red-bg)' } : {}),
          }}>
            {shownText}
            {showCursor && (
              <span style={{
                display: 'inline-block', width: 7, height: 13,
                background: isUser ? 'rgba(255,255,255,0.7)' : 'var(--brand-accent)',
                borderRadius: 1, marginLeft: 3, verticalAlign: 'middle',
                animation: 'cursor-blink 0.9s step-end infinite',
              }} />
            )}
          </div>
        )}

        {/* The model ran out of tokens mid-reply (chatService.js `truncated`).
            Held back while a translation is pending so the note comes in the
            reply's own language. */}
        {message.truncated && !message.awaitingTranslation && (
          <div className="chat-translation" lang={message.translationLang || textLang(text)}>
            <div className="chat-translation__warning" role="note">
              {chatTranslationLabels(message.translationLang || textLang(text)).cutShort}
            </div>
          </div>
        )}

        {/* Mounted only once the translation has landed, so a flagged reply
            starts with its English open. */}
        {(message.english || message.translationFailed) && (
          <TranslationNote message={message} isUser={isUser} />
        )}

        {/* Timestamp */}
        {timestamp && !showCursor && (
          <span style={{
            fontSize: 10, color: 'var(--text-muted)',
            marginTop: 1,
            paddingLeft: isUser ? 0 : 2,
            paddingRight: isUser ? 2 : 0,
          }}>
            {timestamp}
          </span>
        )}
      </div>

      {/* Spacer for user side */}
      {isUser && <div style={{ width: avatarSz, flexShrink: 0 }} />}
    </div>
  );
}
